import type { Chapter, PageListItem } from "@shared/entities";
import { Link } from "@tanstack/react-router";
import { StatusDot } from "./chips";

const SECTION_LABEL: Record<string, string> = { design: "Design", systems: "Systems", qa: "QA", chapters: "Chapters", other: "Other" };

/**
 * The book's contents. Chapters are listed directly: this is a codex about a
 * ten-chapter game, so the ten chapters are the spine, not a submenu.
 */
export function Sidebar({ chapters, pages, onNavigate }: { chapters: Chapter[]; pages: PageListItem[]; onNavigate?: () => void }) {
	const sections = new Map<string, PageListItem[]>();
	for (const p of pages) sections.set(p.section, [...(sections.get(p.section) ?? []), p]);

	const item = "flex items-center gap-2 rounded-md px-2.5 py-1.5 text-sm transition-colors hover:bg-[var(--color-ink-800)]";
	const active = { className: "bg-[var(--color-ink-800)] text-[var(--color-gold-400)]" };
	const heading = "mb-1 px-2.5 text-xs font-medium";
	const muted = { color: "var(--color-ink-400)" };

	return (
		<nav className="flex h-full flex-col gap-6 overflow-y-auto p-4" aria-label="Codex contents">
			<Link to="/" className="px-2.5 font-display text-lg font-semibold tracking-tight" onClick={onNavigate}>
				Chibi Codex
			</Link>

			<div>
				<h2 className={heading} style={muted}>
					Game
				</h2>
				<Link to="/monsters" className={item} activeProps={active} onClick={onNavigate}>
					Monsters
				</Link>
				<Link to="/gear" className={item} activeProps={active} activeOptions={{ exact: true }} onClick={onNavigate}>
					Gear and perks
				</Link>
				<Link to="/gear/catalog" className={item} activeProps={active} onClick={onNavigate}>
					Gear catalog
				</Link>
				<Link to="/skills" className={item} activeProps={active} onClick={onNavigate}>
					Skills and elements
				</Link>
				<Link to="/rules" className={item} activeProps={active} onClick={onNavigate}>
					Rules and numbers
				</Link>
			</div>

			<div>
				<h2 className={heading} style={muted}>
					Chapters
				</h2>
				{chapters.map((ch) => (
					<Link key={ch.id} to="/chapters/$id" params={{ id: ch.id }} className={item} activeProps={active} onClick={onNavigate}>
						<span className="num w-5 text-right text-xs" style={muted}>
							{ch.index}
						</span>
						<span className="min-w-0 flex-1 truncate">{ch.annotation.summary || ch.name}</span>
						<StatusDot status={ch.annotation.status} label={false} />
					</Link>
				))}
			</div>

			<div>
				<div className="mb-1 flex items-center justify-between px-2.5">
					<h2 className="text-xs font-medium" style={muted}>
						Pages
					</h2>
					<Link to="/pages/new" className="text-xs" style={{ color: "var(--color-gold-400)" }} onClick={onNavigate}>
						+ New
					</Link>
				</div>
				{pages.length === 0 && (
					<p className="px-2.5 text-xs" style={muted}>
						No pages yet.
					</p>
				)}
				{[...sections.entries()].map(([section, list]) => (
					<div key={section} className="mb-2">
						<h3 className="px-2.5 pt-1 text-[0.7rem]" style={muted}>
							{SECTION_LABEL[section] ?? section}
						</h3>
						{list.map((p) => (
							<Link key={p.id} to="/pages/$slug" params={{ slug: p.slug }} className={item} activeProps={active} onClick={onNavigate}>
								<span className="truncate">{p.title}</span>
							</Link>
						))}
					</div>
				))}
			</div>
		</nav>
	);
}
